import { GraphQLFieldConfig, GraphQLNonNull } from 'graphql'
import {
  ServerAggregationType,
  ServerAggregation
} from '../types/server-aggregation'

import { Context } from '../shared'
import {
  ServerAggregationArgs,
  serverAggregationArgs
} from '../types/server-aggregation-args'

import {
  ContrastQueryParameters,
  appendServerFiltersToBody
} from '../endpoint-helpers'
import { getRequestedFields } from './helpers/aggregations'

function mapTermBucket({ count, keycode }) {
  return {
    count,
    key: keycode
  }
}

function mapApplicationBucket({ keycode: id, label: name, count }) {
  return {
    count,
    key: {
      id,
      name
    }
  }
}

const MAX_FILTER_ITEMS = 100000
function extractBuckets(mapper) {
  return function bucketMapper(data) {
    return data.filters.length > MAX_FILTER_ITEMS
      ? data.filters.slice(0, MAX_FILTER_ITEMS).map(mapper)
      : data.filters.map(mapper)
  }
}

const extractTermBuckets = extractBuckets(mapTermBucket)
const extractApplicationBuckets = extractBuckets(mapApplicationBucket)

type ServerBucket =
  | 'tags'
  | 'agentversion'
  | 'loglevels'
  | 'environment'
  | 'apps'

export const serverAggregations: GraphQLFieldConfig<
  any,
  Context,
  ServerAggregationArgs
> = {
  description: 'Get the aggregation for a collection of servers',
  type: new GraphQLNonNull(ServerAggregationType),
  args: serverAggregationArgs,
  async resolve(
    _source,
    args,
    context,
    info
  ): Promise<Partial<ServerAggregation>> {
    const { organizationID, filters } = args
    const { fieldNodes } = info

    const requestedFields = getRequestedFields(fieldNodes, 'serverAggregations')

    const hasRequestedField = (field: string) =>
      requestedFields.indexOf(field) > -1

    const params = new ContrastQueryParameters()
    params.append('expand', 'skip_links')

    const mappedFilters = appendServerFiltersToBody(filters)

    const getAggregation = (bucketType: ServerBucket) =>
      context.connector.post(
        `${organizationID}/servers/filters/${bucketType}/listing?${params}`,
        mappedFilters
      )

    const [tags, agentVersions, environments, logLevels, applications] =
      await Promise.all([
        hasRequestedField('tags')
          ? getAggregation('tags').then(extractTermBuckets)
          : Promise.resolve([]),
        hasRequestedField('agentVersions')
          ? getAggregation('agentversion').then(extractTermBuckets)
          : Promise.resolve([]),
        hasRequestedField('environments')
          ? getAggregation('environment').then(extractTermBuckets)
          : Promise.resolve([]),
        hasRequestedField('logLevels')
          ? getAggregation('loglevels').then(extractTermBuckets)
          : Promise.resolve([]),
        hasRequestedField('applications')
          ? getAggregation('apps').then(extractApplicationBuckets)
          : Promise.resolve([])
      ])

    return {
      tags,
      agentVersions,
      environments,
      logLevels,
      applications
    }
  }
}
